"use client";

import { useEffect, useRef } from "react";
import {
  AppSearchInteractionOption,
  FilterBar,
  useEmitAppSearchInteractionEvent,
  useRouterParams,
} from "@zapier/shared";
import { useAppsFilterConfig } from "./useAppsFilterConfig";

interface Props {
  isDisabled?: boolean;
  showFiltersButton?: boolean;
}

// Persists across the tab session (not just this component's lifetime) so the
// default doesn't come back on a later mount within the same session, e.g.
// navigating away and back after explicitly clearing the chip.
const SESSION_FLAG_KEY = "appsFilterBar_defaultSuppressed";

// sessionStorage can throw (e.g. Safari private mode, disabled storage,
// sandboxed iframes) even when `window` exists. These helpers let that
// failure mean "no persistence" instead of crashing the filter bar.
function getSessionFlag(key: string): string | undefined {
  try {
    return globalThis.window?.sessionStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
}

function setSessionFlag(key: string, value: string): void {
  try {
    globalThis.window?.sessionStorage.setItem(key, value);
  } catch {
    // Ignore: persistence is a nice-to-have, not required for correctness.
  }
}

// On first landing with no `shared=` we pre-apply `latest_public` so the
// Version chip is visible. Clearing the chip drops the param; in that state
// the data layer shows all app versions (public + private merged).
export function AppsFilterBar({ isDisabled, showFiltersButton }: Props) {
  const { filters, quickFilters, quickFilterContext, onFilterInteraction } =
    useAppsFilterConfig();
  const { emit } = useEmitAppSearchInteractionEvent();
  const { shared, setParams } = useRouterParams();

  const hasApplied = useRef(false);
  // If the URL already has a `page` param on mount, the user intentionally
  // navigated to a specific state (e.g. after clearing the chip and paging).
  // Don't override that state with the default filter.
  const hadExplicitPageOnMount = useRef(
    globalThis.window !== undefined &&
      new URLSearchParams(globalThis.window.location.search).has("page"),
  );

  // Track explicit clearing of the chip (a truthy `shared` becoming falsy) so
  // we know not to re-apply the default later in this session.
  const previousShared = useRef(shared);
  useEffect(() => {
    const wasShared = previousShared.current;
    previousShared.current = shared;
    if (wasShared && !shared && globalThis.window !== undefined) {
      setSessionFlag(SESSION_FLAG_KEY, "true");
    }
  }, [shared]);

  useEffect(() => {
    if (hasApplied.current) return;
    hasApplied.current = true;
    if (hadExplicitPageOnMount.current) return;
    if (globalThis.window !== undefined && getSessionFlag(SESSION_FLAG_KEY)) {
      return;
    }
    if (!shared) {
      setParams({ shared: "latest_public" });
    }
  }, [shared, setParams]);

  return (
    <FilterBar
      namespace="apps-filter-bar"
      filters={filters}
      quickFilters={quickFilters}
      quickFilterContext={quickFilterContext}
      isDisabled={isDisabled}
      showFiltersButton={showFiltersButton}
      onFilterInteraction={onFilterInteraction}
      onSearch={(value) => {
        if (value.trim().length > 0) {
          emit(AppSearchInteractionOption.Keyword);
        }
      }}
    />
  );
}
